import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoUploader } from "@/components/PhotoUploader";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";

const BODY_TYPES = ["Sedan", "SUV", "Truck", "Van", "Coupe", "Hatchback", "Wagon", "Convertible"];
const TRANSMISSIONS = ["Automatic", "Manual", "CVT"];
const FUELS = ["Gasoline", "Diesel", "Hybrid", "Electric"];
const DRIVETRAINS = ["FWD", "RWD", "AWD", "4WD"];

const empty = {
  year: new Date().getFullYear(), make: "", model: "", price: "", mileage: "",
  body_type: "SUV", transmission: "Automatic", fuel_type: "Gasoline", drivetrain: "FWD",
  exterior_color: "", description: "", photos: [], main_photo_index: 0, status: "available",
  show_just_arrived: true,
};

export const VehicleFormModal = ({ open, onClose, vehicle, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (vehicle) setForm({ ...empty, ...vehicle });
    else setForm(empty);
  }, [vehicle, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSelect = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      year: parseInt(form.year, 10),
      price: parseFloat(form.price),
      mileage: parseInt(form.mileage, 10),
    };
    try {
      if (vehicle) await api.put(`/admin/vehicles/${vehicle.id}`, payload);
      else await api.post("/admin/vehicles", payload);
      toast.success(vehicle ? "Vehicle updated" : "Vehicle added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white" data-testid="vehicle-form-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-white">{vehicle ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Year"><Input type="number" required value={form.year} onChange={set("year")} className="input-dark" data-testid="form-year" /></Field>
            <Field label="Make"><Input required value={form.make} onChange={set("make")} className="input-dark" placeholder="Ford" data-testid="form-make" /></Field>
            <Field label="Model"><Input required value={form.model} onChange={set("model")} className="input-dark" placeholder="F-150" data-testid="form-model" /></Field>
            <Field label="Price ($)"><Input type="number" required value={form.price} onChange={set("price")} className="input-dark" data-testid="form-price" /></Field>
            <Field label="Mileage (km)"><Input type="number" required value={form.mileage} onChange={set("mileage")} className="input-dark" data-testid="form-mileage" /></Field>
            <Field label="Color"><Input value={form.exterior_color} onChange={set("exterior_color")} className="input-dark" placeholder="Black" data-testid="form-color" /></Field>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SelectField label="Body Type" value={form.body_type} onChange={setSelect("body_type")} options={BODY_TYPES} tid="form-body-type" />
            <SelectField label="Transmission" value={form.transmission} onChange={setSelect("transmission")} options={TRANSMISSIONS} tid="form-transmission" />
            <SelectField label="Fuel" value={form.fuel_type} onChange={setSelect("fuel_type")} options={FUELS} tid="form-fuel" />
            <SelectField label="Drivetrain" value={form.drivetrain} onChange={setSelect("drivetrain")} options={DRIVETRAINS} tid="form-drivetrain" />
          </div>

          <Field label="Description">
            <Textarea value={form.description} onChange={set("description")} rows={3} className="input-dark" placeholder="Condition, features, history…" data-testid="form-description" />
          </Field>

          <div>
            <Label className="text-xs font-semibold text-slate-400 mb-2 block">Photos (up to 12)</Label>
            <PhotoUploader
              photos={form.photos}
              mainIndex={form.main_photo_index}
              onChange={(photos, main) => setForm((f) => ({ ...f, photos, main_photo_index: main }))}
            />
          </div>

          <SelectField label="Status" value={form.status} onChange={setSelect("status")} options={["available", "coming_soon", "sold"]} tid="form-status" />

          <div className="flex items-center justify-between rounded-lg border border-slate-700 bg-slate-900 px-4 py-3">
            <div>
              <Label className="text-sm font-semibold text-white">Show "Just Arrived" badge</Label>
              <p className="text-xs text-slate-400 mt-0.5">Appears for 14 days after adding, on the card badge and the home "Fresh to the Lot" strip.</p>
            </div>
            <Switch
              checked={form.show_just_arrived !== false}
              onCheckedChange={(v) => setForm((f) => ({ ...f, show_just_arrived: v }))}
              data-testid="form-show-just-arrived"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-900" data-testid="form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700 font-semibold" data-testid="form-save">
              {saving ? <Loader2 className="animate-spin" size={18} /> : vehicle ? "Save Changes" : "Add Vehicle"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-400">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const SelectField = ({ label, value, onChange, options, tid }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-400">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-white capitalize" data-testid={tid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o} className="capitalize">{o.replace(/_/g, " ")}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
